import { Component, Input, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { SvgIconComponent } from 'src/app/ui/svg-icon/svg-icon.component';
import { IProductHandlingUnit } from '../../../produits/models/products.models';
import { quantityInBaseUnitsTranslationParams } from 'src/app/shared/utils/handling-unit.utils';

/**
 * Compact "Quantity details" tooltip, styled to match the pricing tooltip
 * (app-price-details-tooltip). Used from the qty column info icon in both the
 * order details table and the side-by-side correction panel.
 *
 * The parent resolves the selected handling unit (selection logic differs per
 * surface) and passes it in via [selectedHu].
 */
@Component({
  selector: 'app-qty-details-tooltip',
  standalone: true,
  imports: [CommonModule, TranslateModule, SvgIconComponent],
  templateUrl: './qty-details-tooltip.component.html',
  styleUrls: ['./qty-details-tooltip.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class QtyDetailsTooltipComponent {
  @Input() item!: {
    quantity?: number | null;
    quantity_handling_unit?: number | null;
    quantity_preferred_selling_unit?: number | null;
    quantity_preferred_selling_unit_name?: string | null;
    handling_units?: IProductHandlingUnit[];
    handling_unit_raw_phrase?: string;
    handling_unit_raw_quantity?: number;
    /** Raw quantity/unit the AI actually sent, before the WB-7249 sub-unit conversion re-expressed it per selling unit. */
    ai_extracted_quantity?: number | null;
    ai_extracted_unit?: string | null;
    /** True when the sub-unit conversion (needs_unit_conversion + conversion_coefficient) fired for this line. */
    ai_unit_conversion_applied?: boolean;
  };
  @Input() selectedHu?: IProductHandlingUnit | null;

  /**
   * What the AI actually sent, e.g. "20 pallets" (null when nothing was parsed).
   * When the sub-unit conversion fired, `handling_unit_raw_quantity` is already
   * the *converted* (selling-unit) quantity, not what the AI sent — so this
   * falls back to the true pre-conversion snapshot (`ai_extracted_quantity` /
   * `ai_extracted_unit`) instead.
   */
  get parsedData(): string | null {
    if (this.item?.ai_unit_conversion_applied) {
      const rawQty = this.item?.ai_extracted_quantity;
      const rawUnit = this.item?.ai_extracted_unit;
      if (rawQty == null && !rawUnit) return null;
      return `${rawQty ?? ''} ${rawUnit || ''}`.trim();
    }
    const phrase = this.item?.handling_unit_raw_phrase;
    const qty = this.item?.handling_unit_raw_quantity;
    const hasQty = qty != null && qty !== 0;
    if (!hasQty && !phrase) return null;
    return hasQty ? `${qty} ${phrase || ''}`.trim() : phrase || null;
  }

  /** Shows a small note explaining why the parsed value differs from the order quantity. */
  get showConversionNote(): boolean {
    return !!this.item?.ai_unit_conversion_applied;
  }

  get hasPreferredSellingUnit(): boolean {
    return (
      this.item?.quantity_preferred_selling_unit !== null &&
      this.item?.quantity_preferred_selling_unit !== undefined
    );
  }

  get hasBaseUnitsConversion(): boolean {
    const mult = this.selectedHu?.multiplier_from_base_unit;
    return mult != null && Number(mult) > 0;
  }

  /** i18n params for the "Quantity in base units" caption. */
  get baseUnitsParams(): { sellingQty: string; multiplier: string; baseUnits: string } {
    const baseUnit = this.item?.handling_units?.find((u) => u.is_base_unit);
    return quantityInBaseUnitsTranslationParams(
      this.item?.quantity_handling_unit,
      this.selectedHu,
      baseUnit
    );
  }
}
